/**
 * HelpWidget · Asistente Pelip. Mini-chat flotante que AYUDA a usar la app y da
 * soporte (función assistant-support: Haiku + manual, escala a humano). Distinto de
 * los chats de experto (panel derecho por etapa). Fallback "Escribir al equipo"
 * (contact-support) siempre visible: nadie queda atrapado hablando con el bot.
 */
import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { HelpCircle, X, Send, Loader2, Sparkles, LifeBuoy } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

type Msg = { role: 'user' | 'assistant'; content: string; escalated?: boolean };

// Chips de arranque por contexto de ruta.
const CHIP_SETS: { match: RegExp; chips: string[] }[] = [
  { match: /presupuesto/, chips: ['¿Cómo genero el presupuesto?', '¿Qué es el Cap 10?'] },
  { match: /financiacion/, chips: ['¿Cómo funciona el simulador de financiación?'] },
  { match: /rodaje/, chips: ['¿Qué son las jornadas?'] },
  { match: /festivales|distribucion/, chips: ['¿Cómo elijo festivales?'] },
  { match: /localizaciones|mapa/, chips: ['¿Cómo ubico las localizaciones?'] },
  { match: /(proyectos|subir|^\/$)/, chips: ['¿Cómo empiezo?', '¿Cómo subo un guion?'] },
];
function chipsFor(path: string): string[] {
  return CHIP_SETS.find(c => c.match.test(path))?.chips ?? ['¿Cómo empiezo?', '¿Qué puedo hacer aquí?'];
}

// Render mínimo: [texto](/ruta) → <Link>, **negrita** → <strong>, saltos de línea.
// Reinyecta el ?job= actual en las rutas internas (si no lo llevan) para no perder el proyecto.
function renderRich(text: string, onNavigate: () => void, job: string | null) {
  const withJob = (to: string) => (!job || /[?&]job=/.test(to)) ? to : to + (to.includes('?') ? '&' : '?') + 'job=' + job;
  return text.split('\n').map((line, li) => {
    const nodes: React.ReactNode[] = [];
    const re = /\[([^\]]+)\]\((\/[^)]*)\)|\*\*([^*]+)\*\*/g;
    let last = 0, m: RegExpExecArray | null, i = 0;
    while ((m = re.exec(line)) !== null) {
      if (m.index > last) nodes.push(line.slice(last, m.index));
      if (m[1] && m[2]) nodes.push(<Link key={`${li}-${i}`} to={withJob(m[2])} onClick={onNavigate} className="text-pelip-accent underline underline-offset-2">{m[1]}</Link>);
      else if (m[3]) nodes.push(<strong key={`${li}-${i}`}>{m[3]}</strong>);
      last = m.index + m[0].length; i++;
    }
    if (last < line.length) nodes.push(line.slice(last));
    return <p key={li} className={li > 0 ? 'mt-1.5' : ''}>{nodes}</p>;
  });
}

interface HelpWidgetProps {
  collapsed: boolean;
}

export default function HelpWidget({ collapsed }: HelpWidgetProps) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [humanMode, setHumanMode] = useState(false);
  const location = useLocation();
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' }); }, [messages, sending]);

  async function ask(text: string) {
    const q = text.trim();
    if (!q || sending) return;
    setInput('');
    setMessages(m => [...m, { role: 'user', content: q }]);
    setSending(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const url = import.meta.env.VITE_SUPABASE_URL as string;
      const anon = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), 25000);
      const r = await fetch(`${url}/functions/v1/assistant-support`, {
        method: 'POST',
        headers: { apikey: anon, Authorization: `Bearer ${session?.access_token ?? anon}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: q, page: location.pathname }),
        signal: ctrl.signal,
      }).finally(() => clearTimeout(timer));
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d?.error) throw new Error(d?.error || `HTTP ${r.status}`);
      setMessages(m => [...m, { role: 'assistant', content: d.reply, escalated: d.escalated }]);
    } catch {
      setMessages(m => [...m, { role: 'assistant', content: 'Uf, ahora mismo no puedo responder. Prueba de nuevo o escribe al equipo con el botón de abajo.' }]);
    } finally {
      setSending(false);
    }
  }

  async function sendToHuman() {
    const q = input.trim();
    if (!q) return;
    setSending(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const url = import.meta.env.VITE_SUPABASE_URL as string;
      const anon = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
      const r = await fetch(`${url}/functions/v1/contact-support`, {
        method: 'POST',
        headers: { apikey: anon, Authorization: `Bearer ${session?.access_token ?? anon}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: q, email: session?.user?.email, page: location.pathname }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d?.error) throw new Error(d?.error || `HTTP ${r.status}`);
      toast.success('¡Mensaje enviado! Te respondemos por email pronto.');
      setInput(''); setHumanMode(false); setOpen(false);
    } catch (e) {
      toast.error('No se pudo enviar', { description: e instanceof Error ? e.message : 'Inténtalo de nuevo.' });
    } finally { setSending(false); }
  }

  const chips = chipsFor(location.pathname);
  const job = new URLSearchParams(location.search).get('job');

  return (
    <>
      {open && (
        <div className="fixed bottom-20 left-5 z-50 w-[380px] max-w-[calc(100vw-2.5rem)] h-[520px] max-h-[calc(100vh-7rem)] bg-pelip-bg-elev border border-pelip-border rounded-xl shadow-2xl flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-pelip-border bg-pelip-bg-elev-2/50">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-pelip-accent/15 flex items-center justify-center"><Sparkles size={15} className="text-pelip-accent" /></div>
              <div>
                <h3 className="text-sm font-semibold text-pelip-text leading-tight">Asistente Pelip</h3>
                <p className="text-[11px] text-pelip-text-faint leading-tight">te ayudo a usar la app</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-pelip-text-faint hover:text-pelip-text-soft"><X size={16} /></button>
          </div>

          {/* Mensajes */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {messages.length === 0 && !humanMode && (
              <div className="text-sm text-pelip-text-muted">
                <p>Hola 👋 Pregúntame cómo usar Pelip, dónde está algo, o qué significa un botón o un número.</p>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {chips.map(c => (
                    <button key={c} onClick={() => ask(c)} className="text-xs px-2.5 py-1 rounded-full border border-pelip-border text-pelip-text-muted hover:border-pelip-accent hover:text-pelip-text transition-colors">{c}</button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
                <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${m.role === 'user' ? 'bg-pelip-accent/15 text-pelip-text' : 'bg-pelip-bg-elev-2 text-pelip-text-soft'}`}>
                  {m.role === 'assistant' ? renderRich(m.content, () => setOpen(false), job) : m.content}
                  {m.escalated && <p className="mt-1.5 text-[11px] text-emerald-600 dark:text-emerald-400 flex items-center gap-1"><LifeBuoy size={11} /> avisado al equipo</p>}
                </div>
              </div>
            ))}
            {sending && <div className="flex justify-start"><div className="bg-pelip-bg-elev-2 rounded-lg px-3 py-2"><Loader2 size={14} className="animate-spin text-pelip-text-faint" /></div></div>}
          </div>

          {/* Input */}
          <div className="border-t border-pelip-border p-3">
            {humanMode ? (
              <div>
                <p className="text-[11px] text-pelip-text-muted mb-1.5">Escribe al equipo — te respondemos por email.</p>
                <textarea value={input} onChange={e => setInput(e.target.value)} rows={2} autoFocus placeholder="Cuéntanos qué necesitas…"
                  className="w-full bg-pelip-bg border border-pelip-border rounded-md px-3 py-2 text-sm text-pelip-text placeholder-slate-500 resize-none focus:outline-none focus:ring-2 focus:ring-ring" />
                <div className="flex gap-2 mt-2">
                  <button onClick={() => setHumanMode(false)} className="text-xs text-pelip-text-faint hover:text-pelip-text-muted px-2">← Volver al asistente</button>
                  <button onClick={sendToHuman} disabled={sending || !input.trim()} className="ml-auto inline-flex items-center gap-1.5 bg-pelip-primary hover:bg-pelip-primary-hover disabled:opacity-50 text-white text-sm font-medium rounded-md px-3 py-1.5">
                    {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Enviar
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-end gap-2">
                  <textarea value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(input); } }}
                    rows={1} placeholder="Escribe tu pregunta…"
                    className="flex-1 bg-pelip-bg border border-pelip-border rounded-md px-3 py-2 text-sm text-pelip-text placeholder-slate-500 resize-none focus:outline-none focus:ring-2 focus:ring-ring max-h-24" />
                  <button onClick={() => ask(input)} disabled={sending || !input.trim()} className="shrink-0 w-9 h-9 rounded-md bg-pelip-primary hover:bg-pelip-primary-hover disabled:opacity-50 text-white flex items-center justify-center">
                    {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                  </button>
                </div>
                <button onClick={() => setHumanMode(true)} className="mt-2 text-[11px] text-pelip-text-faint hover:text-pelip-accent">¿Prefieres una persona? <span className="underline">Escribir al equipo</span></button>
              </>
            )}
          </div>
        </div>
      )}
      <button onClick={() => setOpen(o => !o)} title="Asistente Pelip"
        className={`fixed bottom-6 ${collapsed ? 'left-20' : 'left-64'} z-50 w-11 h-11 rounded-full bg-pelip-bg-elev border border-pelip-border text-pelip-text-muted hover:text-pelip-text hover:border-pelip-border-strong shadow-lg flex items-center justify-center transition-all duration-200`}>
        {open ? <X size={18} /> : <HelpCircle size={20} />}
      </button>
    </>
  );
}
